// Precondition: ft-dialog component must be loaded
// Postcondition: Custom element class for Apple Spotlight-style search dialog
// Invariants: Dialog opens on ft-spotlight-open event or Cmd/Ctrl+K, singleton per page

import { icon } from "../../../shared/icons.js";
import { searchFilers } from "../../lib/financeapis.js";
import { createLogger } from "../../lib/logger.js";

const log = createLogger("ft-spotlight-search");

if (!customElements.get("ft-spotlight-search")) {
  interface Filer {
    cik: string;
    name: string;
    tickers?: string;
  }

  interface FilerSearchResult {
    entity: string;
    cik: string;
    tickers?: string;
  }

  interface SpotlightOptions {
    placeholder?: string;
    action?: (filer: Filer) => void;
  }

  class FtSpotlightSearch extends HTMLElement {
    private dialog: FtDialog | null = null;
    private input: HTMLInputElement | null = null;
    private autocomplete: HTMLElement | null = null;
    private debounceTimer: number | undefined;
    private selectedIndex: number = -1;
    private boundKeyHandler: (e: KeyboardEvent) => void;
    private boundOpenHandler: (e: Event) => void;
    private action: ((filer: Filer) => void) | null = null;

    constructor() {
      super();
      this.attachShadow({ mode: "open" });
      this.boundKeyHandler = this.handleGlobalKeydown.bind(this);
      this.boundOpenHandler = (e: Event) =>
        this.open((e as CustomEvent<SpotlightOptions>).detail);
    }

    connectedCallback(): void {
      log.debug("connected");
      // Singleton enforcement: only one instance allowed per document
      const existing = document.querySelectorAll("ft-spotlight-search");
      if (existing.length > 1) {
        throw new Error(
          "ft-spotlight-search: Only one instance allowed per page. Remove duplicate elements.",
        );
      }

      this.render();
      this.setupReferences();
      this.setupEventListeners();
    }

    disconnectedCallback(): void {
      log.debug("disconnected");
      window.removeEventListener("keydown", this.boundKeyHandler);
      window.removeEventListener("ft-spotlight-open", this.boundOpenHandler);
    }

    // Precondition: Shadow DOM attached
    // Postcondition: Renders HTML structure into shadow DOM
    private render(): void {
      const style = document.createElement("style");
      style.textContent = this.getStyles();

      const template = document.createElement("template");
      template.innerHTML = this.getTemplate();

      if (this.shadowRoot) {
        this.shadowRoot.appendChild(style);
        this.shadowRoot.appendChild(template.content.cloneNode(true));
      }
    }

    // Precondition: None
    // Postcondition: Returns CSS string for shadow DOM
    private getStyles(): string {
      return `
      :host {
        display: contents;
      }

      .search-input {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 16px;
      }

      .icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        flex-shrink: 0;
        color: var(--text-secondary, #6b7280);
      }

      input {
        flex: 1;
        padding: 0;
        border: none;
        background: transparent;
        font-size: 16px;
        color: var(--text, #1f2937);
        outline: none;
        font-family: inherit;
      }

      input::-webkit-search-cancel-button {
        display: none;
      }

      input::placeholder {
        color: var(--text-tertiary, #9ca3af);
      }

      .clear-btn {
        display: none;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        padding: 0;
        background: var(--bg-tertiary, #f3f4f6);
        border: none;
        border-radius: 50%;
        cursor: pointer;
        color: var(--text-secondary, #6b7280);
        transition: all 0.15s ease;
        flex-shrink: 0;
      }

      .clear-btn svg {
        width: 14px;
        height: 14px;
      }

      .clear-btn:hover {
        color: var(--text, #1f2937);
        background: var(--bg-elevated, #e5e7eb);
      }

      input:not(:placeholder-shown) ~ .clear-btn {
        display: flex;
      }

      .autocomplete {
        padding: 0;
        margin: 0;
        list-style: none;
        border-top: 1px solid var(--border, #e5e7eb);
        max-height: 320px;
        overflow-y: auto;
      }

      .autocomplete[hidden] {
        display: none;
      }

      .autocomplete li {
        padding: 8px 16px;
        cursor: pointer;
        font-size: 14px;
        color: var(--text, #1f2937);
        border-bottom: 1px solid var(--border, #e5e7eb);
      }

      .autocomplete li:last-child {
        border-bottom: none;
      }

      .autocomplete li:hover,
      .autocomplete li[aria-selected="true"] {
        background: var(--tab-active-bg, #f3f4f6);
      }

      .autocomplete strong {
        color: var(--text, #1f2937);
        font-weight: 600;
      }

      .autocomplete.loading {
        padding: 16px;
        color: var(--text-tertiary, #9ca3af);
        font-size: 14px;
      }

      .autocomplete.loading::before {
        content: "Searching...";
      }

      @media (prefers-color-scheme: dark) {
        input {
          color: var(--text, #f3f4f6);
        }

        .autocomplete {
          border-top-color: var(--border, #374151);
        }

        .autocomplete li {
          color: var(--text, #f3f4f6);
          border-bottom-color: var(--border, #374151);
        }
      }
    `;
    }

    // Precondition: None
    // Postcondition: Returns HTML template string
    private getTemplate(): string {
      return `
      <ft-dialog no-header>
        <div class="search-input">
          <span class="icon" aria-hidden="true">${icon('search', 24)}</span>
          <input
            type="search"
            placeholder="Search companies, tickers, CIKs..."
            autocomplete="off"
            role="combobox"
            aria-label="Search"
            aria-autocomplete="list"
            aria-controls="autocomplete"
            aria-expanded="false"
            aria-haspopup="listbox"
          />
          <button class="clear-btn" type="button" aria-label="Clear search">${icon('x', 14)}</button>
        </div>
        <ul id="autocomplete" class="autocomplete" role="listbox" hidden></ul>
      </ft-dialog>
    `;
    }

    // Precondition: Shadow DOM rendered
    // Postcondition: References to elements stored
    private setupReferences(): void {
      this.dialog =
        this.shadowRoot?.querySelector<FtDialog>("ft-dialog") ?? null;
      this.input = this.shadowRoot?.querySelector("input") as HTMLInputElement;
      this.autocomplete = this.shadowRoot?.querySelector(
        "#autocomplete",
      ) as HTMLElement;
    }

    // Precondition: References set up
    // Postcondition: Event listeners attached
    private setupEventListeners(): void {
      // Global keyboard shortcut: Cmd/Ctrl+K
      window.addEventListener("keydown", this.boundKeyHandler);

      // Listen for custom event to open spotlight (with optional detail)
      window.addEventListener("ft-spotlight-open", this.boundOpenHandler);

      // Focus input when dialog opens
      if (this.dialog) {
        this.dialog.addEventListener("opened", () => {
          requestAnimationFrame(() => {
            this.input?.focus();
            this.input?.select();
          });
        });

        // Clear on close
        this.dialog.addEventListener("closed", () => {
          if (this.input) this.input.value = "";
          this.hideAutocomplete();
        });
      }

      // Input event - debounced search
      this.input?.addEventListener("input", () => {
        clearTimeout(this.debounceTimer);
        const query = this.input?.value.trim() || "";
        this.debounceTimer = window.setTimeout(() => this.search(query), 300);
      });

      // Clear button
      const clearBtn = this.shadowRoot?.querySelector(
        ".clear-btn",
      ) as HTMLButtonElement;
      clearBtn?.addEventListener("click", (e) => {
        e.preventDefault();
        if (this.input) {
          this.input.value = "";
          this.input.focus();
        }
        this.hideAutocomplete();
      });

      // Keyboard navigation
      this.input?.addEventListener("keydown", (e) =>
        this.handleInputKeydown(e),
      );

      // Click on autocomplete item
      this.autocomplete?.addEventListener("click", (e) => {
        const item = (e.target as HTMLElement).closest(
          "[data-cik]",
        ) as HTMLElement | null;
        if (item?.dataset.cik) {
          this.selectCompany(
            item.dataset.cik,
            item.dataset.name,
            item.dataset.tickers,
          );
        }
      });
    }

    // Precondition: Event is KeyboardEvent
    // Postcondition: Opens spotlight on Cmd/Ctrl+K
    private handleGlobalKeydown(e: KeyboardEvent): void {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        this.open();
      }
    }

    // Precondition: Event is KeyboardEvent from input
    // Postcondition: Handles arrow keys, Enter, Escape
    private handleInputKeydown(e: KeyboardEvent): void {
      const items = this.autocomplete?.querySelectorAll('[role="option"]');
      if (!items?.length) {
        // Enter with no autocomplete - search directly
        if (e.key === "Enter") {
          e.preventDefault();
          this.submitSearch();
        }
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1);
        this.updateSelection(items);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.updateSelection(items);
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (this.selectedIndex >= 0) {
          const item = items[this.selectedIndex] as HTMLElement;
          if (item.dataset.cik) {
            this.selectCompany(
              item.dataset.cik,
              item.dataset.name,
              item.dataset.tickers,
            );
          }
        } else {
          this.submitSearch();
        }
      } else if (e.key === "Escape") {
        this.hideAutocomplete();
      }
    }

    // Precondition: query is string
    // Postcondition: Fetches and displays autocomplete results
    private async search(query: string): Promise<void> {
      if (query.length < 2) {
        this.hideAutocomplete();
        return;
      }

      log.log("search", { query });
      if (this.autocomplete) {
        this.autocomplete.innerHTML = "";
        this.autocomplete.classList.add("loading");
        this.autocomplete.hidden = false;
        this.input?.setAttribute("aria-expanded", "true");
      }

      const result = await searchFilers(query);
      this.autocomplete?.classList.remove("loading");

      if (!result.ok) {
        log.error("search failed", { query, error: result.error });
        this.hideAutocomplete();
        return;
      }

      const filers = result.data.filers || [];
      log.debug("search success", { query, results: filers.length });
      this.showAutocomplete(filers as FilerSearchResult[]);
    }

    // Precondition: companies is array of company objects
    // Postcondition: Displays autocomplete dropdown with first item selected
    private showAutocomplete(
      companies: Array<{ entity: string; cik: string; tickers?: string }>,
    ): void {
      if (!this.autocomplete || !companies.length) {
        this.hideAutocomplete();
        return;
      }

      this.autocomplete.innerHTML = companies
        .map((c, i) => {
          const name = c.entity.split("(")[0].trim();
          const tickersDisplay = c.tickers
            ? `<strong>${c.tickers}</strong> — `
            : "";
          const tickersAttr = c.tickers ? ` data-tickers="${c.tickers}"` : "";
          const selected =
            i === 0 ? ' aria-selected="true"' : ' aria-selected="false"';
          return `<li role="option" id="option-${i}" data-cik="${c.cik}" data-name="${name}"${tickersAttr}${selected}>${tickersDisplay}${name}</li>`;
        })
        .join("");
      this.autocomplete.hidden = false;
      this.selectedIndex = 0;
      this.input?.setAttribute("aria-expanded", "true");
      this.input?.setAttribute("aria-activedescendant", "option-0");
    }

    // Precondition: None
    // Postcondition: Hides autocomplete dropdown and resets aria state
    private hideAutocomplete(): void {
      if (!this.autocomplete) return;
      this.autocomplete.hidden = true;
      this.autocomplete.innerHTML = "";
      this.autocomplete.classList.remove("loading");
      this.selectedIndex = -1;
      this.input?.setAttribute("aria-expanded", "false");
      this.input?.removeAttribute("aria-activedescendant");
    }

    // Precondition: items is NodeList of option elements
    // Postcondition: Updates visual selection state and aria-activedescendant
    private updateSelection(items: NodeListOf<Element>): void {
      items.forEach((item, i) => {
        item.setAttribute(
          "aria-selected",
          i === this.selectedIndex ? "true" : "false",
        );
        if (i === this.selectedIndex) {
          (item as HTMLElement).scrollIntoView({ block: "nearest" });
          this.input?.setAttribute("aria-activedescendant", `option-${i}`);
        }
      });
      if (this.selectedIndex < 0) {
        this.input?.removeAttribute("aria-activedescendant");
      }
    }

    // Precondition: cik is CIK string
    // Postcondition: Calls action callback or navigates to company page
    private selectCompany(cik: string, name?: string, tickers?: string): void {
      log.debug("selectCompany", { cik, name });
      this.hideAutocomplete();
      if (this.action) {
        this.action({ cik, name: name || "", tickers });
      } else {
        window.location.href = "/companies/" + String(cik).padStart(10, "0");
      }
    }

    // Precondition: Input has value
    // Postcondition: Navigates to first result or shows error
    private async submitSearch(): Promise<void> {
      const value = this.input?.value.trim();
      if (!value) return;

      log.log("submitSearch", { value });
      if (/^\d{1,10}$/.test(value)) {
        log.debug("submitSearch direct CIK", { cik: value });
        window.location.href = "/companies/" + value.padStart(10, "0");
        return;
      }

      const result = await searchFilers(value);
      if (!result.ok) {
        log.error("submitSearch failed", { value, error: result.error });
        return;
      }
      const filers = result.data.filers || [];
      if (filers.length) {
        log.debug("submitSearch navigating", { cik: filers[0].cik });
        window.location.href = "/companies/" + filers[0].cik;
      } else {
        log.warn("submitSearch no results", { value });
      }
    }

    // Public API

    // Precondition: Dialog reference exists
    // Postcondition: Dialog is opened with optional action callback
    open(options?: SpotlightOptions): void {
      log.debug("open", { hasAction: !!options?.action });
      this.action = options?.action || null;
      if (this.input) {
        this.input.placeholder =
          options?.placeholder || "Search companies, tickers, CIKs...";
      }
      this.dialog?.open();
    }

    // Precondition: Dialog reference exists
    // Postcondition: Dialog is closed
    close(): void {
      this.dialog?.close();
    }

    // Precondition: Dialog reference exists
    // Postcondition: Dialog is toggled
    toggle(): void {
      this.dialog?.toggle();
    }
  }

  customElements.define("ft-spotlight-search", FtSpotlightSearch);
}
